import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Button, Form } from 'react-bootstrap';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';

const PasswordResetForm = ({ hasLabel }) => {
  // State
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get token from URL
  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  // Token validation on mount
  useEffect(() => {
    if (!token) {
      navigate('/errors/404', { replace: true });
      return;
    }
    // Call backend to validate token (without changing password)
    fetch('/https://helpdeskapi.fidaglobal.com/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password: '___dummy___' }) // dummy password
    })
      .then(async res => {
        if (!res.ok) {
          const data = await res.json();
          if (data.error && (data.error.toLowerCase().includes('expired') || data.error.toLowerCase().includes('invalid'))) {
            navigate('/errors/404', { replace: true });
          }
        }
      })
      .catch(() => {
        navigate('/errors/404', { replace: true });
      });
    // eslint-disable-next-line
  }, []);

  // Handler
  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!', { theme: 'colored' });
      return;
    }
    if (!token) {
      toast.error('Invalid or missing reset token.', { theme: 'colored' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://helpdeskapi.fidaglobal.com/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: formData.password })
      });
      if (res.ok) {
        toast.success('Password reset successful! Please log in with your new password.', { theme: 'colored' });
        setTimeout(() => navigate('/authentication/simple/login'), 2000);
      } else {
        const data = await res.json();
        if (data.error && (data.error.toLowerCase().includes('expired') || data.error.toLowerCase().includes('invalid'))) {
          navigate('/errors/404', { replace: true });
        } else {
          toast.error(data.error || 'Failed to reset password.', { theme: 'colored' });
        }
      }
    } catch (err) {
      toast.error('Network error. Please try again.', { theme: 'colored' });
    }
    setLoading(false);
  };

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Form
      className={classNames('mt-3', { 'text-left': hasLabel })}
      onSubmit={handleSubmit}
    >
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>New Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? 'New Password' : ''}
          value={formData.password}
          name="password"
          onChange={handleFieldChange}
          type="password"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Confirm Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? 'Confirm Password' : ''}
          value={formData.confirmPassword}
          name="confirmPassword"
          onChange={handleFieldChange}
          type="password"
        />
      </Form.Group>

      <Button
        type="submit"
        className="w-100"
        disabled={!formData.password || !formData.confirmPassword || loading}
      >
        {loading ? 'Resetting...' : 'Reset Password'}
      </Button>
    </Form>
  );
};

PasswordResetForm.propTypes = {
  hasLabel: PropTypes.bool
};

PasswordResetForm.defaultProps = {
  hasLabel: false
};

export default PasswordResetForm;
